import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, GitBranch, X } from 'lucide-react';
import {
  createTask,
  fetchTaskCreateLookups,
  type TaskCreateLookups,
} from '@/api/tasks';
import { useI18n } from '@/i18n';
import { useErrorToast } from '@/lib/notify';

type FormState = {
  powerplantId: string;
  taskcategoryId: string;
  taskassignees: string[];
  tasktitle: string;
  taskdescription: string;
  taskpriority: string;
  taskduedate: string;
};

export type TaskCreateInitialValues = Partial<FormState>;
export type TaskCreatePrefillValues = TaskCreateInitialValues & {
  powerplantName?: string;
};
export type TaskCreateMode = 'default' | 'duplicate' | 'related';
export type TaskCreateRouteState = {
  initialValues?: TaskCreatePrefillValues;
  mode?: TaskCreateMode;
  sourceTaskUuidId?: string;
  sourceTaskLabel?: string;
};

const initialForm: FormState = {
  powerplantId: '',
  taskcategoryId: '',
  taskassignees: [],
  tasktitle: '',
  taskdescription: '',
  taskpriority: '1',
  taskduedate: '',
};

type TaskCreateFormProps = {
  onCreated?: () => void;
  onCancel?: () => void;
  modal?: boolean;
  showCloseButton?: boolean;
  initialValues?: TaskCreatePrefillValues;
  mode?: TaskCreateMode;
  sourceTaskUuidId?: string;
  sourceTaskLabel?: string;
};

export function TaskCreateForm({
  onCreated,
  onCancel,
  modal = false,
  showCloseButton = true,
  initialValues,
  mode = 'default',
  sourceTaskUuidId,
  sourceTaskLabel,
}: TaskCreateFormProps) {
  const { t } = useI18n();
  const assigneeDropdownRef = useRef<HTMLDivElement | null>(null);
  const [lookups, setLookups] = useState<TaskCreateLookups | null>(null);
  const [form, setForm] = useState<FormState>(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);
  const [success, setSuccess] = useState<string | null>(null);
  const [assigneesOpen, setAssigneesOpen] = useState(false);

  useEffect(() => {
    fetchTaskCreateLookups()
      .then(setLookups)
      .catch(() => setError(t('tasks.lookupsError')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    if (!lookups) return;

    setForm((current) => {
      const next = { ...current };

      if (typeof initialValues?.powerplantId === 'string') {
        next.powerplantId = initialValues.powerplantId;
      } else if (typeof initialValues?.powerplantName === 'string') {
        const matchedPlant = lookups.powerplants.find(
          (plant) => plant.name.trim().toLocaleLowerCase('tr-TR') === initialValues.powerplantName?.trim().toLocaleLowerCase('tr-TR'),
        );
        if (matchedPlant) {
          next.powerplantId = String(matchedPlant.id);
        }
      }
      if (typeof initialValues?.taskcategoryId === 'string') {
        next.taskcategoryId = initialValues.taskcategoryId;
      }
      if (Array.isArray(initialValues?.taskassignees)) {
        next.taskassignees = initialValues.taskassignees;
      }
      if (typeof initialValues?.tasktitle === 'string') {
        next.tasktitle = initialValues.tasktitle;
      }
      if (typeof initialValues?.taskdescription === 'string') {
        next.taskdescription = initialValues.taskdescription;
      }
      if (typeof initialValues?.taskpriority === 'string') {
        next.taskpriority = initialValues.taskpriority;
      }
      if (typeof initialValues?.taskduedate === 'string') {
        next.taskduedate = initialValues.taskduedate;
      }

      return next;
    });
  }, [initialValues, lookups]);

  const canSubmit = useMemo(() => {
    return (
      form.powerplantId &&
      form.taskcategoryId &&
      form.taskassignees.length > 0 &&
      form.tasktitle.trim().length >= 3 &&
      form.taskdescription.trim().length >= 3 &&
      !saving
    );
  }, [form, saving]);

  const selectedAssignees = useMemo(
    () =>
      (lookups?.taskusers ?? []).filter((user) =>
        form.taskassignees.includes(String(user.id)),
      ),
    [form.taskassignees, lookups?.taskusers],
  );

  useEffect(() => {
    if (!assigneesOpen) return undefined;

    function handlePointerDown(event: MouseEvent) {
      if (!assigneeDropdownRef.current?.contains(event.target as Node)) {
        setAssigneesOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setAssigneesOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [assigneesOpen]);

  function toggleAssignee(userId: string) {
    setForm((state) => {
      const exists = state.taskassignees.includes(userId);
      return {
        ...state,
        taskassignees: exists
          ? state.taskassignees.filter((id) => id !== userId)
          : [...state.taskassignees, userId],
      };
    });
  }

  function resetForm() {
    setForm(initialForm);
    setAssigneesOpen(false);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        powerplantId: Number(form.powerplantId),
        taskcategoryId: Number(form.taskcategoryId),
        taskassignees: form.taskassignees.map(Number),
        tasktitle: form.tasktitle.trim(),
        taskdescription: form.taskdescription.trim(),
        taskpriority: Number(form.taskpriority),
        taskduedate: form.taskduedate || null,
        relatedSourceUuidId: mode === 'related' ? sourceTaskUuidId : undefined,
      };
      const res = await createTask(payload);
      setSuccess(t('tasks.createdSuccess', { id: res.task.id }));
      resetForm();
      onCreated?.();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? t('tasks.createError'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-4 text-sm text-gray-500">{t('common.loading')}</div>;

  return (
    <div className={modal ? '' : 'mx-auto w-full max-w-3xl'}>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-800">{t('tasks.createTitle')}</h1>
        {onCancel && showCloseButton && (
          <button
            onClick={onCancel}
            className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
            aria-label={t('common.close')}
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {mode !== 'default' && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <div className="flex items-center gap-2 font-semibold">
            <GitBranch className="h-4 w-4" />
            {mode === 'duplicate' ? t('tasks.createModes.duplicate') : t('tasks.createModes.related')}
          </div>
          {mode === 'duplicate' ? (
            <div className="mt-1 text-xs text-blue-700">
              {t('tasks.createModeDuplicateHelp')}
            </div>
          ) : (
            <div className="mt-1 text-xs text-blue-700">
              {t('tasks.createModeRelatedHelp', { task: sourceTaskLabel ?? '-' })}
            </div>
          )}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4 rounded-xl border bg-white p-4 shadow-sm md:p-6">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">{t('tasks.powerPlant')}</label>
          <select
            value={form.powerplantId}
            onChange={(e) => setForm((s) => ({ ...s, powerplantId: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            required
          >
            <option value="">{t('tasks.select')}</option>
            {lookups?.powerplants.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">{t('tasks.categoryField')}</label>
          <select
            value={form.taskcategoryId}
            onChange={(e) => setForm((s) => ({ ...s, taskcategoryId: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            required
          >
            <option value="">{t('tasks.select')}</option>
            {lookups?.taskcategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.description}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">{t('tasks.assigneesField')}</label>
          <div ref={assigneeDropdownRef} className="relative">
            <button
              type="button"
              onClick={() => setAssigneesOpen((open) => !open)}
              className={`flex min-h-11 w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition ${
                assigneesOpen
                  ? 'border-blue-500 ring-2 ring-blue-100'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              aria-haspopup="listbox"
              aria-expanded={assigneesOpen}
            >
              <span className="pr-3 text-gray-700">
                {selectedAssignees.length > 0
                  ? selectedAssignees.map((user) => user.fullname).join(', ')
                  : t('tasks.select')}
              </span>
              <ChevronDown
                className={`h-4 w-4 flex-shrink-0 text-gray-500 transition ${assigneesOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {selectedAssignees.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedAssignees.map((user) => (
                  <span
                    key={user.id}
                    className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
                  >
                    {user.fullname}
                  </span>
                ))}
              </div>
            )}

            {assigneesOpen && (
              <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
                <div className="max-h-64 overflow-y-auto p-2">
                  {lookups?.taskusers.map((u) => {
                    const checked = form.taskassignees.includes(String(u.id));
                    return (
                      <label
                        key={u.id}
                        className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAssignee(String(u.id))}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="flex-1">{u.fullname}</span>
                        {checked && <Check className="h-4 w-4 text-blue-600" />}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-400">Checkbox ile birden fazla kisi secilebilir.</p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">{t('tasks.titleField')}</label>
          <input
            type="text"
            value={form.tasktitle}
            onChange={(e) => setForm((s) => ({ ...s, tasktitle: e.target.value }))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            placeholder={t('tasks.titlePlaceholder')}
            required
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">{t('tasks.description')}</label>
          <textarea
            value={form.taskdescription}
            onChange={(e) => setForm((s) => ({ ...s, taskdescription: e.target.value }))}
            className="min-h-28 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            placeholder={t('tasks.descriptionPlaceholder')}
            required
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{t('tasks.priority')}</label>
            <select
              value={form.taskpriority}
              onChange={(e) => setForm((s) => ({ ...s, taskpriority: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            >
              <option value="1">{t('tasks.priorityLabel.low')}</option>
              <option value="2">{t('tasks.priorityLabel.middle')}</option>
              <option value="3">{t('tasks.priorityLabel.high')}</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{t('tasks.dueDate')}</label>
            <input
              type="date"
              value={form.taskduedate}
              onChange={(e) => setForm((s) => ({ ...s, taskduedate: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {success && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            {success}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={resetForm}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
            disabled={saving}
          >
            {t('tasks.clear')}
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? t('tasks.creating') : t('tasks.createButton')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function TaskCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (location.state ?? {}) as TaskCreateRouteState;

  return (
    <TaskCreateForm
      onCancel={() => navigate(-1)}
      initialValues={routeState.initialValues}
      mode={routeState.mode ?? 'default'}
      sourceTaskUuidId={routeState.sourceTaskUuidId}
      sourceTaskLabel={routeState.sourceTaskLabel}
    />
  );
}
